import React, { useState } from 'react';
import { ActivityIndicator, Platform, ScrollView, Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useRouter } from 'expo-router';
import SolidButton from '@/components/General/SolidButton';
import SheetModal from '@/components/General/SheetModal';
import Unicon from '@/components/General/Unicon';
import MilestoneDefinitionList from '@/components/Milestones/MilestoneDefinitionList';
import OrganizationMilestoneHistory from '@/components/Milestones/OrganizationMilestoneHistory';
import { Query } from '@/utils/react-query/Queries';
import { MILESTONES_ENABLED, MILESTONE_SCOPES } from '@/config/milestones';
import { canCreateMilestones, canViewMilestoneManagement } from '@/utils/milestonePermissions';
import useMilestoneMutations from '@/utils/hooks/useMilestoneMutations';
import { showAlert } from '@/utils/showAlert';

/**
 * Organization Milestones: Achievements history. Definition management
 * (built-in catalog + custom series) lives behind the "Manage Definitions"
 * drawer — a side sheet on web, a page-sheet modal on native.
 */
export default function MilestonesScreen() {
    const router = useRouter();
    const { data: loggedInUser, isLoading: userLoading } = Query.people.loggedInUser();
    const organizationID = loggedInUser?.organizationID;
    const allowed = canViewMilestoneManagement(loggedInUser);

    const { data: catalog, isLoading: catalogLoading } = Query.milestones.catalog();
    const orgQuery = Query.milestones.definitions({ organizationID, scope: MILESTONE_SCOPES.ORGANIZATION, options: { enabled: allowed } });
    const individualQuery = Query.milestones.definitions({ organizationID, scope: MILESTONE_SCOPES.INDIVIDUAL, options: { enabled: allowed } });

    const { setEnabled } = useMilestoneMutations({ organizationID });

    const [definitionsOpen, setDefinitionsOpen] = useState(false);

    if (userLoading) {
        return <View style={styles.center}><ActivityIndicator /></View>;
    }

    if (!MILESTONES_ENABLED || !allowed) {
        return (
            <View style={styles.center} testID="milestones-requires-permission">
                <Unicon name="lock-closed-outline" size={40} colorVariant="textSecondary" />
                <Text style={styles.noAccessText}>You don&apos;t have permission to manage milestones.</Text>
            </View>
        );
    }

    const orgDefinitions = (orgQuery.data?.pages ?? []).flatMap(p => p?.definitions ?? []);
    const individualDefinitions = (individualQuery.data?.pages ?? []).flatMap(p => p?.definitions ?? []);
    const definitionsLoading = orgQuery.isLoading || individualQuery.isLoading;
    const loadMore = (query) => {
        if (query.hasNextPage && !query.isFetchingNextPage) query.fetchNextPage();
    };

    const handleToggle = (definition, nextEnabled) => {
        const doToggle = () => setEnabled.mutate({
            milestoneID: definition.id,
            enabled: nextEnabled,
            expectedRevision: definition.configurationRevision,
        });
        if (nextEnabled) {
            showAlert({
                title: `Enable "${definition.name}"?`,
                subtitle: 'Enabling counts all matching history — existing activity may immediately award tiers and trigger one catch-up celebration.',
                confirmText: 'Enable',
                onConfirm: doToggle,
            });
        } else {
            doToggle();
        }
    };

    const openDefinition = (definition) => router.push(`/settings/organization/milestones/${definition.id}`);

    return (
        <>
        <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer} keyboardShouldPersistTaps="handled">
            {/* Achievements — org-scoped earned badges + live progress */}
            <View style={styles.section}>
                <View style={styles.sectionHeader}>
                    <Unicon name="trophy-outline" size={18} colorVariant="gold" />
                    <Text style={styles.sectionTitle}>Achievements</Text>
                    <View style={styles.sectionSpacer} />
                    <SolidButton
                        testID="manage-definitions-button"
                        size="sm"
                        variant="muted"
                        type="primary"
                        text="Manage Definitions"
                        iconName="ribbon-outline"
                        iconBefore
                        onPress={() => setDefinitionsOpen(true)}
                    />
                </View>
                <OrganizationMilestoneHistory organizationID={organizationID} accountID={loggedInUser?.accountID} />
            </View>
        </ScrollView>

        {/* Definitions management — side drawer on web, sheet modal native. */}
        <SheetModal
            isOpen={definitionsOpen}
            title="Manage Definitions"
            subtitle="Enable, review, and create milestone series"
            cancelText="Close"
            onClose={() => setDefinitionsOpen(false)}
            width={560}
            headerRight={Platform.OS === 'web' && canCreateMilestones(loggedInUser) ? (
                <SolidButton
                    testID="create-milestone-button"
                    size="sm"
                    text="New Milestone"
                    iconName="add"
                    iconBefore
                    onPress={() => router.push('/settings/organization/milestones/create')}
                />
            ) : null}
        >
            <View style={styles.sheetBody} testID="milestone-definitions-section">
                {/* Native: the sheet header is too narrow for a header action —
                    the button crowds the title, so it lives in the body. */}
                {Platform.OS !== 'web' && canCreateMilestones(loggedInUser) && (
                    <View style={styles.createRow}>
                        <SolidButton
                            testID="create-milestone-button"
                            size="sm"
                            text="New Milestone"
                            iconName="add"
                            iconBefore
                            onPress={() => router.push('/settings/organization/milestones/create')}
                        />
                    </View>
                )}
                <Text style={styles.sectionHint}>
                    Enabling a built-in or previously-disabled series counts all matching history — existing activity may award tiers immediately and trigger one catch-up celebration. Tap a series to edit it.
                </Text>

                <Text style={styles.scopeLabel}>Organization Milestones</Text>
                <MilestoneDefinitionList
                    scope={MILESTONE_SCOPES.ORGANIZATION}
                    definitions={orgDefinitions}
                    catalog={catalog}
                    loggedInUser={loggedInUser}
                    loading={definitionsLoading || catalogLoading}
                    pendingToggleID={setEnabled.isPending ? setEnabled.variables?.milestoneID : null}
                    onOpen={openDefinition}
                    onToggleEnabled={handleToggle}
                />
                {orgQuery.hasNextPage && (
                    <SolidButton testID="org-definitions-more" variant="outlined" type="textSecondary" text="Load More" processing={orgQuery.isFetchingNextPage} onPress={() => loadMore(orgQuery)} />
                )}

                <Text style={styles.scopeLabel}>Individual Milestones</Text>
                <MilestoneDefinitionList
                    scope={MILESTONE_SCOPES.INDIVIDUAL}
                    definitions={individualDefinitions}
                    catalog={catalog}
                    loggedInUser={loggedInUser}
                    loading={definitionsLoading || catalogLoading}
                    pendingToggleID={setEnabled.isPending ? setEnabled.variables?.milestoneID : null}
                    onOpen={openDefinition}
                    onToggleEnabled={handleToggle}
                />
                {individualQuery.hasNextPage && (
                    <SolidButton testID="individual-definitions-more" variant="outlined" type="textSecondary" text="Load More" processing={individualQuery.isFetchingNextPage} onPress={() => loadMore(individualQuery)} />
                )}
            </View>
        </SheetModal>
        </>
    );
}

const styles = StyleSheet.create(({ colors, fonts, spacing }) => ({
    container: {
        flex: 1,
        backgroundColor: 'transparent',
    },
    contentContainer: {
        padding: spacing.md,
        gap: spacing.lg,
        _web: {
            maxWidth: 900,
            alignSelf: 'center',
            width: '100%',
        },
    },
    center: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'transparent',
        gap: spacing.md,
        padding: spacing.lg,
    },
    noAccessText: {
        fontSize: 15,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        textAlign: 'center',
    },
    section: {
        gap: spacing.sm,
    },
    sectionHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.sm,
    },
    sectionTitle: {
        fontSize: 16,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    sectionSpacer: {
        flex: 1,
    },
    sectionHint: {
        fontSize: 12,
        fontFamily: fonts.body,
        color: colors.textTertiary,
        lineHeight: 16,
    },
    scopeLabel: {
        fontSize: 13,
        fontFamily: fonts.heading,
        color: colors.textSecondary,
        marginTop: spacing.sm,
    },
    sheetBody: {
        gap: spacing.sm,
        paddingBottom: spacing.lg,
    },
    createRow: {
        flexDirection: 'row',
    },
}));
